import { memo, useEffect, useRef, useState } from "react";
import { Input, Select } from "antd";
import {
	AlertTriangle,
	CheckCircle2,
	Eye,
	MessageSquare,
	Pencil,
	Send,
	ShieldCheck,
	ThumbsUp,
	Trash2,
	X
} from "lucide-react";

import {
	PlanReviewFeedbackDecision,
	PlanReviewFeedbackErrorAttribution,
	PlanReviewFeedbackReason
} from "constants/field-values";
import { PLAN_REVIEW_FEEDBACK_REASON_OPTIONS } from "constants/constants";
import { feedbackReasonLabel } from "utils/plan-review-finding-review";

/** One plan_review_feedback row, as it arrives nested under the finding. */
export interface PlanReviewFeedbackRow {
	id: string;
	decision?: string | null;
	error_attribution?: string | null;
	reason?: string | null;
	comment?: string | null;
	user_id: string;
	/** Role of the author when the row was saved. Authority roles mean reviewer feedback. */
	user_role: string;
	created_at: string;
	updated_at: string;
}

export interface PlanReviewFeedbackSubmission {
	decision: PlanReviewFeedbackDecision;
	errorAttribution: PlanReviewFeedbackErrorAttribution | null;
	reason: PlanReviewFeedbackReason | null;
	comment: string;
	/** Set when the user is editing their own saved row instead of writing a new one. */
	editingFeedbackId: string | null;
}

/** A decision that the modal asks the panel to open with, for example Incorrect from the "Dismiss" button. */
export interface PlanReviewFeedbackDecisionRequest {
	decision: PlanReviewFeedbackDecision;
	/** Changes on every request, so the same decision can be requested twice. */
	requestId: number;
}

export interface PlanReviewFeedbackSectionProps {
	/** Panel title, for example "Reviewer Feedback" or "Applicant Feedback". */
	title: string;
	/** Text under the title. */
	subtitle: string;
	/** The saved row to display, or null to show the empty form. */
	savedFeedback: PlanReviewFeedbackRow | null;
	submitting: boolean;
	deleting: boolean;
	/** True when savedFeedback was written by the logged user, who can edit or delete it. */
	canChangeSavedFeedback: boolean;
	/** Reviewers say which part of the analysis was off. Applicants pick a reason instead. */
	asksWhichPartWasOff: boolean;
	/** Applicants must pick a reason when the rating is Partially Correct or Incorrect. */
	requiresReason: boolean;
	/** Label of the Incorrect button, for example "Incorrect · Dismiss". */
	incorrectLabel?: string;
	/** Label of the submit button when the rating is Incorrect, for example "Dismiss & Move to Marked Incorrect". */
	incorrectSubmitLabel?: string;
	/** Opens the form with this decision selected and scrolls the panel into view. */
	decisionRequest?: PlanReviewFeedbackDecisionRequest | null;
	/** Resolves true when the row was persisted, which clears the form. */
	onSubmit: (submission: PlanReviewFeedbackSubmission) => Promise<boolean>;
	/** Deletes savedFeedback. Resolves true when the row was removed. */
	onDelete: () => Promise<boolean>;
}

// Three-state rating. One decision + a disambiguation follow-up.
const DECISION_OPTIONS: {
	value: PlanReviewFeedbackDecision;
	label: string;
	icon: typeof CheckCircle2;
	cls: string;
}[] = [
	{
		value: PlanReviewFeedbackDecision.CONFIRMED,
		label: "Confirmed Accurate",
		icon: ThumbsUp,
		cls: "prfs-decision-confirmed"
	},
	{
		value: PlanReviewFeedbackDecision.PARTIALLY_CORRECT,
		label: "Partially Correct",
		icon: Pencil,
		cls: "prfs-decision-partial"
	},
	{ value: PlanReviewFeedbackDecision.INCORRECT, label: "Incorrect", icon: X, cls: "prfs-decision-incorrect" }
];

// Disambiguation: what the reviewer picks becomes error_attribution, which is what makes
// per-model accuracy queryable (reading the plans vs. applying the requirement).
const ATTRIBUTION_OPTIONS: { value: PlanReviewFeedbackErrorAttribution; label: string; icon: typeof CheckCircle2 }[] = [
	{ value: PlanReviewFeedbackErrorAttribution.PERCEPTION, label: "Observation was wrong", icon: Eye },
	{ value: PlanReviewFeedbackErrorAttribution.REASONING, label: "Compliance call was wrong", icon: ShieldCheck },
	{ value: PlanReviewFeedbackErrorAttribution.BOTH, label: "Both", icon: AlertTriangle }
];

const humanize = (raw?: string | null): string =>
	raw
		? raw
				.replace(/_/g, " ")
				.toLowerCase()
				.replace(/\b\w/g, c => c.toUpperCase())
		: "";

const formatDate = (iso?: string | null): string => (iso ? new Date(iso).toLocaleString() : "—");

const decisionLabel = (decision?: string | null): string | null =>
	decision ? (DECISION_OPTIONS.find(option => option.value === decision)?.label ?? humanize(decision)) : null;

const attributionLabel = (attribution?: string | null): string | null =>
	attribution
		? (ATTRIBUTION_OPTIONS.find(option => option.value === attribution)?.label ?? humanize(attribution))
		: null;

const decisionTagClass = (decision?: string | null): string => {
	if (decision === PlanReviewFeedbackDecision.CONFIRMED) return "prfs-history-tag-up";
	if (decision === PlanReviewFeedbackDecision.PARTIALLY_CORRECT) return "prfs-history-tag-warn";
	return "prfs-history-tag-down";
};

// Icon + colour for the saved attribution chip. One subsystem off (perception OR
// reasoning) = warning amber; both off = fail red.
const attributionVisual = (attribution?: string | null): { icon: typeof CheckCircle2; cls: string } => {
	switch (attribution) {
		case PlanReviewFeedbackErrorAttribution.PERCEPTION:
			return { icon: Eye, cls: "prfs-history-attr-warning" };
		case PlanReviewFeedbackErrorAttribution.REASONING:
			return { icon: ShieldCheck, cls: "prfs-history-attr-warning" };
		case PlanReviewFeedbackErrorAttribution.BOTH:
			return { icon: AlertTriangle, cls: "prfs-history-attr-fail" };
		default:
			return { icon: CheckCircle2, cls: "prfs-history-attr-pass" };
	}
};

/**
 * Feedback panel inside the plan review finding modal ("Reviewer Feedback" for authority
 * roles, "Applicant Feedback" for the other roles): a three-state decision plus, on the
 * non-confirming paths, which part of the analysis was off (reviewers), a reason from a fixed
 * list and a comment.
 *
 * The panel shows the latest row of the user's group. "Update feedback" writes a new row, so the
 * finding keeps the full history of feedback. The author of that row can also edit it in place
 * ("Edit") or delete it.
 *
 * Owns only the form; the modal owns the mutations. Form state is per-mount, so the modal
 * re-keys the panel per finding to reset it.
 */
const PlanReviewFeedbackSection = ({
	title,
	subtitle,
	savedFeedback,
	submitting,
	deleting,
	canChangeSavedFeedback,
	asksWhichPartWasOff,
	requiresReason,
	incorrectLabel,
	incorrectSubmitLabel,
	decisionRequest,
	onSubmit,
	onDelete
}: PlanReviewFeedbackSectionProps) => {
	const sectionRef = useRef<HTMLDivElement>(null);
	const [decision, setDecision] = useState<PlanReviewFeedbackDecision | null>(null);
	const [errorAttribution, setErrorAttribution] = useState<PlanReviewFeedbackErrorAttribution | null>(null);
	const [reason, setReason] = useState<PlanReviewFeedbackReason | null>(null);
	const [comment, setComment] = useState("");
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [isEditing, setIsEditing] = useState(false);
	// True when the open form changes the saved row ("Edit") instead of writing a new one.
	const [isEditingSavedFeedback, setIsEditingSavedFeedback] = useState(false);
	// True only after a successful save in THIS panel — drives the ephemeral "Feedback
	// received" banner, so it never shows just because saved feedback already exists.
	const [justSubmitted, setJustSubmitted] = useState(false);

	const resetForm = () => {
		setDecision(null);
		setErrorAttribution(null);
		setReason(null);
		setComment("");
		setSubmitError(null);
		setIsEditing(false);
		setIsEditingSavedFeedback(false);
	};

	// Opens the form with the requested decision, for example when the applicant clicks
	// "Dismiss / Mark Incorrect" at the top of the modal.
	useEffect(() => {
		if (!decisionRequest) return;
		setDecision(decisionRequest.decision);
		setErrorAttribution(null);
		setSubmitError(null);
		setIsEditing(true);
		setIsEditingSavedFeedback(false);
		sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
	}, [decisionRequest]);

	const fillFormWithSavedFeedback = () => {
		if (!savedFeedback) return;
		setDecision((savedFeedback.decision as PlanReviewFeedbackDecision) ?? null);
		setErrorAttribution((savedFeedback.error_attribution as PlanReviewFeedbackErrorAttribution) ?? null);
		setReason((savedFeedback.reason as PlanReviewFeedbackReason) ?? null);
		setComment(savedFeedback.comment ?? "");
		setSubmitError(null);
		setIsEditing(true);
	};

	// Re-opens the form pre-filled with the saved feedback. Saving it writes a new row.
	const handleStartUpdate = () => {
		fillFormWithSavedFeedback();
		setIsEditingSavedFeedback(false);
	};

	// Re-opens the form pre-filled with the user's own saved row. Saving it changes that row.
	const handleStartEdit = () => {
		fillFormWithSavedFeedback();
		setIsEditingSavedFeedback(true);
	};

	const handleDelete = async () => {
		const removed = await onDelete();
		if (removed) resetForm();
	};

	const handleSubmit = async () => {
		if (decision === null) return;
		const isNotConfirmed = decision !== PlanReviewFeedbackDecision.CONFIRMED;
		if (isNotConfirmed && requiresReason && reason === null) {
			setSubmitError("Please select a reason when rating a finding as Partially Correct or Incorrect.");
			return;
		}
		// On the non-confirming paths, the reviewer must either pick which part was off or
		// explain in a comment (the "can't pick a clean attribution" escape hatch).
		const hasNoExplanation = errorAttribution === null && reason === null && comment.trim().length === 0;
		if (isNotConfirmed && asksWhichPartWasOff && hasNoExplanation) {
			setSubmitError("Tell Ollie which part was off, or add a short comment.");
			return;
		}
		setSubmitError(null);

		const saved = await onSubmit({
			decision,
			errorAttribution: isNotConfirmed && asksWhichPartWasOff ? errorAttribution : null,
			reason: isNotConfirmed ? reason : null,
			comment: comment.trim(),
			editingFeedbackId: isEditingSavedFeedback && savedFeedback ? savedFeedback.id : null
		});
		if (!saved) return;

		resetForm();
		setJustSubmitted(true);
	};

	const decisionButtonLabel = (option: (typeof DECISION_OPTIONS)[number]): string =>
		option.value === PlanReviewFeedbackDecision.INCORRECT && incorrectLabel ? incorrectLabel : option.label;

	const selectSubmitLabel = (): string => {
		if (isEditingSavedFeedback) return "Save changes";
		if (decision === PlanReviewFeedbackDecision.INCORRECT && incorrectSubmitLabel) return incorrectSubmitLabel;
		return "Submit feedback";
	};
	const submitLabel = selectSubmitLabel();

	return (
		<div className="prfs-section" ref={sectionRef}>
			<div className="prfs-header">
				<div className="prfs-header-icon-container">
					<MessageSquare className="prfs-header-icon" />
				</div>
				<div>
					<span className="prfs-header-title">{title}</span>
					<p className="prfs-header-subtitle">{subtitle}</p>
				</div>
			</div>

			{savedFeedback && !isEditing ? (
				/* ── Saved feedback: green confirmation + edit / delete ─────────────── */
				<div className="prfs-received">
					{/* Ephemeral: shown only right after a successful save; gone on reopen. */}
					{justSubmitted && (
						<div className="prfs-received-banner">
							<CheckCircle2 className="prfs-received-icon" />
							<div>
								<p className="prfs-received-title">Feedback received</p>
								<p className="prfs-received-subtitle">Thank you for helping improve to Ollie.</p>
							</div>
						</div>
					)}

					{(() => {
						const attribution = savedFeedback.error_attribution ?? null;
						const attr = attributionLabel(attribution);
						const attrVisual = attributionVisual(attribution);
						const AttrIcon = attrVisual.icon;
						const savedReasonLabel = feedbackReasonLabel(savedFeedback.reason);
						return (
							<div className="prfs-saved">
								<div className="prfs-history-head">
									<span className={`prfs-history-tag ${decisionTagClass(savedFeedback.decision)}`}>
										{decisionLabel(savedFeedback.decision) ?? "Feedback"}
									</span>
									{attr && (
										<span className={`prfs-history-attr ${attrVisual.cls}`}>
											<AttrIcon className="prfs-history-attr-icon" />
											{attr}
										</span>
									)}
									{savedReasonLabel && <span className="prfs-history-reason">{savedReasonLabel}</span>}
									{savedFeedback.updated_at && <p>{formatDate(savedFeedback.updated_at)}</p>}
								</div>
								{savedFeedback.comment && <p className="prfs-history-comment">{savedFeedback.comment}</p>}
							</div>
						);
					})()}

					<div className="prfs-submit-row">
						<button className="prfs-cancel-button" onClick={handleStartUpdate}>
							<Pencil size={14} /> Update feedback
						</button>
						{canChangeSavedFeedback && (
							<>
								<button className="prfs-cancel-button" onClick={handleStartEdit}>
									<Pencil size={14} /> Edit
								</button>
								<button className="prfs-delete-button" onClick={handleDelete} disabled={deleting}>
									<Trash2 size={14} /> {deleting ? "Deleting…" : "Delete"}
								</button>
							</>
						)}
					</div>
				</div>
			) : (
				/* ── Feedback form (create or edit) ─────────────────────────────────── */
				<>
					<div className="prfs-decision-row">
						{DECISION_OPTIONS.map(option => {
							const DecisionIcon = option.icon;
							const isActive = decision === option.value;
							return (
								<button
									key={option.value}
									type="button"
									className={`prfs-decision-btn ${option.cls} ${isActive ? "prfs-decision-btn-active" : ""}`}
									onClick={() => {
										setDecision(option.value);
										if (option.value === PlanReviewFeedbackDecision.CONFIRMED) setErrorAttribution(null);
										setSubmitError(null);
									}}
								>
									<DecisionIcon className="prfs-decision-btn-icon" />
									{decisionButtonLabel(option)}
								</button>
							);
						})}
					</div>

					{/* Disambiguation + comment — only on the two non-confirming paths. */}
					{decision && decision !== PlanReviewFeedbackDecision.CONFIRMED && (
						<>
							{asksWhichPartWasOff && (
								<div className="prfs-attribution">
									<p className="prfs-attribution-label">Which part was off?</p>
									<div className="prfs-attribution-options">
										{ATTRIBUTION_OPTIONS.map(option => {
											const AttributionIcon = option.icon;
											const isActive = errorAttribution === option.value;
											return (
												<button
													key={option.value}
													type="button"
													className={`prfs-attribution-btn ${isActive ? "prfs-attribution-btn-active" : ""}`}
													onClick={() => {
														setErrorAttribution(isActive ? null : option.value);
														setSubmitError(null);
													}}
												>
													<AttributionIcon className="prfs-attribution-btn-icon" />
													{option.label}
												</button>
											);
										})}
									</div>
								</div>
							)}

							<div className="prfs-reason">
								<p className="prfs-attribution-label">{requiresReason ? "Reason (required)" : "Reason"}</p>
								<Select
									className="prfs-reason-select"
									placeholder="Select a reason…"
									value={reason ?? undefined}
									status={submitError && requiresReason && reason === null ? "error" : undefined}
									options={PLAN_REVIEW_FEEDBACK_REASON_OPTIONS.map(option => ({
										value: option.value,
										label: option.label
									}))}
									onChange={(value: PlanReviewFeedbackReason) => {
										setReason(value);
										setSubmitError(null);
									}}
								/>
							</div>

							<p className="prfs-attribution-label">
								{decision === PlanReviewFeedbackDecision.INCORRECT
									? "Reason for incorrect analysis (Optional)"
									: "What's missing or slightly off? (Optional)"}
							</p>
							<Input.TextArea
								className="prfs-comment"
								rows={3}
								placeholder={"Please explain why the AI analysis is incorrect to help us improve..."}
								value={comment}
								onChange={e => setComment(e.target.value)}
							/>
						</>
					)}

					{decision && (
						<>
							{submitError && <p className="prfs-error">{submitError}</p>}
							<div className="prfs-submit-row">
								<button className="prfs-cancel-button" onClick={resetForm}>
									Cancel
								</button>
								<button className="prfs-submit-button" onClick={handleSubmit} disabled={submitting}>
									<Send size={16} color="white" strokeWidth={1.33} /> {submitting ? "Saving…" : submitLabel}
								</button>
							</div>
						</>
					)}
				</>
			)}
		</div>
	);
};

export default memo(PlanReviewFeedbackSection);
